import { useContext, useEffect, HTMLAttributes } from "react";
import { useIntl } from "react-intl";
import SummaryCard from "../summary-card";
import { Link } from "react-router-dom";
import FieldList from "../../field-list";
import { LicenseWithCredits } from "../../../model/entitlement/LicenseWithCredits";
import LicenseUtils from "../../../utils/licensed-item";
import { ModalKeys } from "../../pages/licenses";
import {
  isAboutToExpire,
  isScheduled,
  isValid,
  sortToLatestByCreatedOrUpdated,
} from "../../../util/objectUtil";
import { ButtonGroup } from "react-bootstrap";
import LicenseValidityBadge, {
  resolveLicenseValidity,
} from "../../badges/license-validity-badge";
import UIConfiguration from "../../../ui-configuration/configuration-provider";
import TooltipTrigger from "../../tooltip-trigger";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { LicenseValues } from "../../../localization/license";
import { TotalLabel } from "../summary-card/summary-card-view";
interface LicensesCardDOMProps
  extends Omit<HTMLAttributes<HTMLDivElement>, "title"> {}
export interface LicensesCardProps extends LicensesCardDOMProps {
  isReady: boolean;
  licenses?: LicenseWithCredits[] | null;
  validCount?: number;
  invalidCount?: number;
  expiringCount?: number;
  scheduledCount?: number;
  deactivatedCount?: number;
  onLoadLicenses?: () => {};
  moreAction: () => void;
}

function LicensesCard(props: LicensesCardProps) {
  const {
    licenses,
    onLoadLicenses,
    moreAction,
    validCount,
    invalidCount,
    expiringCount,
    scheduledCount,
    deactivatedCount,
    isReady,
  } = props;
  const intl = useIntl();
  const { conf } = useContext(UIConfiguration);
  const licensesConf =
    conf.functionality && conf.functionality.licenses
      ? conf.functionality.licenses
      : ({} as any);
  const headerButton = {
    label: intl.formatMessage({
      defaultMessage: "View",
      description: "label for the view button",
    }),
    action: moreAction,
  };
  if (!onLoadLicenses && licenses === undefined) {
    throw new Error(
      "UsersCard: Required props missing. Either licenses or onLoadLicenses must be defined"
    );
  }
  const fields: any = {
    total: {
      label: intl.formatMessage(TotalLabel),
      value: licenses ? licenses.length : 0,
    },
  };
  if (invalidCount) {
    fields.invalid = {
      label: intl.formatMessage(LicenseValues.validityStatus.invalid),
      value: invalidCount,
    };
  }
  if (expiringCount) {
    fields.expiring = {
      label: intl.formatMessage(LicenseValues.validityStatus.expiring),
      value: expiringCount,
    };
  }
  if (scheduledCount) {
    fields.scheduled = {
      label: intl.formatMessage(LicenseValues.validityStatus.scheduled),
      value: scheduledCount,
    };
  }
  if (validCount) {
    fields.valid = {
      label: intl.formatMessage(LicenseValues.validityStatus.valid),
      value: validCount,
    };
  }
  if (deactivatedCount) {
    fields.deactivatedCount = {
      label: intl.formatMessage(LicenseValues.validityStatus.deactivated),
      value: deactivatedCount,
    };
  }
  const isLicensesUndefined = licenses === undefined;
  useEffect(() => {
    if (isLicensesUndefined && onLoadLicenses) {
      onLoadLicenses();
    }
  }, [isLicensesUndefined, onLoadLicenses]);

  return (
    <SummaryCard
      onReloadData={() => {
        if (onLoadLicenses) {
          onLoadLicenses();
        }
      }}
      isReady={isReady}
      data-test-licenses-card
      title={intl.formatMessage({
        defaultMessage: "Licenses",
        description: "heading for the card",
      })}
      headerButton={headerButton}
      items={
        licenses && licenses.length
          ? licenses
              // expired
              .filter((l) => !isValid(l) && !isScheduled(l))
              .filter((l: any) => l.active !== false)
              .sort(sortToLatestByCreatedOrUpdated)
              // about to expire
              .concat(
                licenses
                  .filter((l) => isValid(l) && isAboutToExpire(l))
                  .filter((l: any) => l.active !== false)
                  .sort(sortToLatestByCreatedOrUpdated)
              )
              // scheduled
              .concat(
                licenses
                  .filter((l) => !isValid(l) && isScheduled(l))
                  .filter((l: any) => l.active !== false)
                  .sort(sortToLatestByCreatedOrUpdated)
              )
              // valid
              .concat(
                licenses
                  .filter((l) => isValid(l) && !isAboutToExpire(l))
                  .filter((l: any) => l.active !== false)
                  .sort(sortToLatestByCreatedOrUpdated)
              )
              // not active
              .concat(
                licenses
                  .filter((l: any) => l.active === false)
                  .sort(sortToLatestByCreatedOrUpdated)
              )
              .map((l) => {
                const Wrapper = licensesConf.show ? Link : "span";
                const wrapperProps: any = {
                  className:
                    "flex-shrink-1 d-flex justify-content-between align-items-center top-most mr-2",
                };
                if (licensesConf.show) {
                  wrapperProps.to =
                    (("/licenses/" + l.id) as string) + "/" + ModalKeys.show;
                }
                const lName = LicenseUtils.resolveDisplayName(
                  (l as any).licensedItem,
                  intl.formatMessage(
                    LicenseValues.licensedItem.displayName.undefined
                  )
                );
                return (
                  <>
                    <Wrapper {...wrapperProps}>
                      <TooltipTrigger
                        tipKey={"license_" + l.id}
                        tip={lName}
                        placement={"top"}
                      >
                        <span className={"flex-shrink-1 mr-2 truncate"}>
                          {lName}
                        </span>
                      </TooltipTrigger>
                      <LicenseValidityBadge
                        validFrom={l.validFrom}
                        validUntil={l.validUntil}
                        validity={resolveLicenseValidity(l)}
                      />
                    </Wrapper>
                    {licensesConf.usage && (
                      <ButtonGroup size={"sm"} className={"ml-2 float-right"}>
                        {licensesConf.usage && (
                          <Link
                            className={"btn btn-theme-to-secondary"}
                            to={
                              (("/licenses/" + l.id) as string) +
                              "/" +
                              ModalKeys.usage
                            }
                          >
                            {intl.formatMessage({
                              defaultMessage: "Usage",
                              description: "label for the usage button",
                            })}
                          </Link>
                        )}
                      </ButtonGroup>
                    )}
                  </>
                );
              })
          : undefined
      }
      footer={<FieldList inline={true} fields={fields} />}
    >
      {licenses && licenses.length === 0 && (
        <div data-test-no-items className={"text-muted text-center py-4"}>
          <div className={"icon pb-1 mb-3"}>
            <span className="fa-layers fa-fw fa-2x">
              <FontAwesomeIcon
                icon="circle"
                mask={"database"}
                className={"opacity-50"}
                transform="shrink-0 right-5 down-5"
              />
              <FontAwesomeIcon icon="ban" transform="shrink-4 right-5 down-5" />
            </span>
          </div>
          <p className={"mb-0"}>
            {intl.formatMessage({
              defaultMessage: "No licenses to show.",
              description: "display value for no licenses",
            })}
          </p>
        </div>
      )}
    </SummaryCard>
  );
}
export default LicensesCard;
